import { inject } from 'inversify';
import { Injectable } from '../../decorators/injectable';
import { HttpService, type IHttpService } from '../http/http.service';

export interface MetalDetector {
  id: string;
  name: string;
  location?: string | null;
  sensitivityFerrousMm?: number | null;
  sensitivityNonFerrousMm?: number | null;
  sensitivityStainlessMm?: number | null;
  /** Rolling cadence in minutes: 30 (every 30 min) .. 10080 (every 7 days). */
  frequencyMinutes: number;
  /** Notification lead in minutes before the deadline: 10 .. 1440 (< frequencyMinutes). */
  warningMinutes: number;
  /** Optional override of the notification time band ("HH:mm", null/null = tenant window). */
  notifyFromTime?: string | null;
  notifyToTime?: string | null;
  isActive: boolean;
}

export interface MetalDetectionRecord {
  id: string;
  metalDetectorId: string;
  metalDetectorName: string;
  date: string;
  completedAt: string;
  ferrousDetected: boolean;
  nonFerrousDetected: boolean;
  stainlessDetected: boolean;
  rejectionWorking: boolean;
  notes?: string | null;
  performerName: string;
  performedBy?: string | null;
  signatureStorageKey?: string | null;
}

export type MetalDetectionCalendarStatus = 'checked' | 'due' | 'warning' | 'overdue' | 'none';

export interface MetalDetectionStatusResponse {
  calendar: Record<string, Record<string, { status: MetalDetectionCalendarStatus }>>;
  detectorsMetadata?: Record<string, { name: string }>;
  /** Detectors with a check due (or lapsed) that day, and the checked ones. */
  days: Record<string, { scheduledCount: number; completedCount: number }>;
}

export interface CreateMetalDetectorRequest {
  name: string;
  location?: string;
  sensitivityFerrousMm?: number | null;
  sensitivityNonFerrousMm?: number | null;
  sensitivityStainlessMm?: number | null;
  frequencyMinutes: number;
  warningMinutes: number;
  notifyFromTime?: string | null;
  notifyToTime?: string | null;
}

export type UpdateMetalDetectorRequest = Partial<CreateMetalDetectorRequest>;

export interface MetalDetectorActiveStateRequest {
  isActive: boolean;
  endDate?: string;
  label?: string;
}

export interface IMetalDetectorService {
  findAll(): Promise<MetalDetector[]>;
  findActive(): Promise<MetalDetector[]>;
  create(data: CreateMetalDetectorRequest): Promise<MetalDetector>;
  update(id: string, data: UpdateMetalDetectorRequest): Promise<MetalDetector>;
  delete(id: string): Promise<void>;
  setActiveState(id: string, data: MetalDetectorActiveStateRequest): Promise<void>;
  getCalendar(id: string, from: string, to: string): Promise<MetalDetectionStatusResponse>;
  findRecords(id: string, from: string, to: string): Promise<MetalDetectionRecord[]>;
  createRecord(
    id: string,
    data: Omit<
      MetalDetectionRecord,
      'id' | 'metalDetectorId' | 'metalDetectorName' | 'completedAt' | 'performerName'
    > & { signatureStorageKey?: string },
  ): Promise<MetalDetectionRecord>;
}

/**
 * Fonte unica per l'entità MetalDetector (CRUD + stato attivo/spento) e per i
 * suoi record di verifica. Mirror di `services/fryer/fryer.service.ts`:
 * `frontend/src/pages/equipment/` possiede la gestione del dispositivo,
 * `frontend/src/pages/self-check/metal-detection/` importa da qui per la
 * registrazione delle verifiche — mai il contrario.
 */
@Injectable()
export class MetalDetectorService implements IMetalDetectorService {
  constructor(@inject(HttpService) private readonly http: IHttpService) {}

  findAll() {
    return this.http.get<MetalDetector[]>('metal-detectors');
  }

  findActive() {
    return this.http.get<MetalDetector[]>('metal-detectors/active');
  }

  create(data: CreateMetalDetectorRequest) {
    return this.http.post<MetalDetector>('metal-detectors', data);
  }

  update(id: string, data: UpdateMetalDetectorRequest) {
    return this.http.put<MetalDetector>(`metal-detectors/${id}`, data);
  }

  async delete(id: string) {
    await this.http.delete<void>(`metal-detectors/${id}`);
  }

  async setActiveState(id: string, data: MetalDetectorActiveStateRequest) {
    await this.http.patch(`metal-detectors/${id}/active-state`, data);
  }

  getCalendar(id: string, from: string, to: string) {
    return this.http.get<MetalDetectionStatusResponse>(
      `metal-detectors/${id}/calendar?from=${from}&to=${to}`,
    );
  }

  findRecords(id: string, from: string, to: string) {
    return this.http.get<MetalDetectionRecord[]>(
      `metal-detectors/${id}/records?from=${from}&to=${to}`,
    );
  }

  createRecord(id: string, data: Parameters<IMetalDetectorService['createRecord']>[1]) {
    return this.http.post<MetalDetectionRecord>(`metal-detectors/${id}/records`, data);
  }
}
